import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useDuplicateInvoiceReport } from '@/features/invoice_reports/components/ConfirmDuplicateModal/hooks/useDuplicateInvoiceReport';
import { useTranslation } from '@/i18n';
import { formatZeroAmount } from '@/utils/formatAmount';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import styles from './ConfirmDuplicateModal.module.scss';
import { useConfirmDuplicateModal } from './context/ConfirmDuplicateModalContext';

const cx = classnames.bind(styles);

export const ConfirmDuplicateModal: FC = memo(() => {
  const {
    duplicateModalOpen,
    duplicateParams,
    onCloseConfirmDuplicateModal,
    refetch,
    onDrawerClose,
    selectedId,
  } = useConfirmDuplicateModal();

  const { t } = useTranslation();
  const { duplicateReport } = useDuplicateInvoiceReport({
    refetch,
    onDrawerClose,
    selectedId,
  });

  const handleDuplicate = useCallback(async () => {
    if (!duplicateParams) return;
    await duplicateReport(duplicateParams)();
    onCloseConfirmDuplicateModal();
  }, [duplicateReport, duplicateParams, onCloseConfirmDuplicateModal]);

  const cancelButtonProps: ButtonV2Props = useMemo(
    () => ({
      isTertiary: true,
      onClick: onCloseConfirmDuplicateModal,
      size: 'md',
      children: t('cancel'),
    }),
    [onCloseConfirmDuplicateModal, t]
  );

  const duplicateButtonProps: ButtonV2Props = useMemo(
    () => ({
      onClick: handleDuplicate,
      size: 'md',
      children: t('duplicate'),
    }),
    [handleDuplicate, t]
  );

  return (
    <ModalWithoutPortal
      open={duplicateModalOpen}
      onClose={onCloseConfirmDuplicateModal}
      size='md'
      title={t('duplicate')}
      mask
      footer={
        <ConfirmationModalFooter
          cancelButton={cancelButtonProps}
          okButton={duplicateButtonProps}
        />
      }
    >
      <div>
        <p className={cx('confirm-message')}>
          {t('invoice_reports.duplicate_confirm_message')}
        </p>
        <p className={cx('confirm-note')}>
          {t('invoice_reports.duplicate_confirm_note')}
        </p>
        {duplicateParams && (
          <div className={cx('detail-table')}>
            {duplicateParams.number && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.application_number')}
                </span>
                <span className={cx('detail-value')}>
                  {duplicateParams.number}
                </span>
              </div>
            )}
            {duplicateParams.title && (
              <div className={cx('detail-row')}>
                <span className={cx('detail-label')}>
                  {t('invoice_reports.table.columns.title')}
                </span>
                <span className={cx('detail-value')}>
                  {duplicateParams.title}
                </span>
              </div>
            )}
            <div className={cx('detail-row')}>
              <span className={cx('detail-label')}>
                {t('invoice_reports.table.columns.payee')}
              </span>
              <span className={cx('detail-value')}>
                {duplicateParams.apPayeeName}
              </span>
            </div>
            <div className={cx('detail-row')}>
              <span className={cx('detail-label')}>
                {t('invoice_reports.table.columns.total_amount')}
              </span>
              <span className={cx('detail-value')}>
                {formatZeroAmount(duplicateParams.totalAmount) || ''}
              </span>
            </div>
          </div>
        )}
      </div>
    </ModalWithoutPortal>
  );
});

ConfirmDuplicateModal.displayName = 'ConfirmDuplicateModal';
